/**
 * SupplierShortageReport.jsx (iter 137 + 138)
 *
 * Per-supplier scorecard summarising closed-with-shortage receipts +
 * on-time delivery % inside a sliding window (default 90 days).
 * Powers the annual supplier review, on-time scorecard, and
 * credit-claim follow-up workflow.
 *
 * iter138 additions:
 *   - On-time delivery KPI tile + per-supplier column
 *   - Inline pencil button to set a per-supplier claims_email override
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Warning, FileArrowDown, ChartBar, Buildings, ArrowClockwise,
  Clock, PencilSimple, Envelope, Trash, CheckCircle, XCircle, Money,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const WINDOW_OPTIONS = [
  { value: 30,  label: 'Last 30 days'  },
  { value: 90,  label: 'Last 90 days'  },
  { value: 180, label: 'Last 6 months' },
  { value: 365, label: 'Last 12 months' },
];

const REASON_LABELS = {
  supplier_short_ship:   'Supplier short-shipped',
  damaged_on_arrival:    'Damaged on arrival',
  lost_in_transit:       'Lost in transit',
  cancelled_by_supplier: 'Cancelled by supplier',
  other:                 'Other',
  uncategorized:         'Pre-iter135 (no reason captured)',
};

const REASON_COLOURS = {
  supplier_short_ship:   'bg-amber-500',
  damaged_on_arrival:    'bg-rose-500',
  lost_in_transit:       'bg-blue-500',
  cancelled_by_supplier: 'bg-purple-500',
  other:                 'bg-slate-500',
  uncategorized:         'bg-slate-300',
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', {
  year: 'numeric', month: 'short', day: 'numeric',
}) : '—';

const fmtMoney = (n, currency = 'USD') => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  try {
    return Number(n).toLocaleString('en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
};

// Colour-coded on-time % chip — green ≥95, amber ≥85, rose <85, slate when null
const onTimeChip = (pct) => {
  if (pct === null || pct === undefined) {
    return { label: 'No ETA data', cls: 'bg-slate-100 text-slate-600 border-slate-300' };
  }
  if (pct >= 95) return { label: `${pct}%`, cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
  if (pct >= 85) return { label: `${pct}%`, cls: 'bg-amber-100 text-amber-800 border-amber-300' };
  return { label: `${pct}%`, cls: 'bg-rose-100 text-rose-800 border-rose-300' };
};

const downloadCSV = (data) => {
  const rows = [
    ['Supplier', 'Total receipts', 'On-time %', 'On-time', 'Late', 'No-ETA',
     'Receipts closed short', 'Units outstanding', 'Late credit due', 'Currency',
     'Penalty %/wk', 'Penalty cap %', 'Top reason', 'Claims email', 'Last incident'],
    ...data.suppliers.map((s) => {
      const topReason = Object.entries(s.by_reason || {}).sort((a, b) => b[1] - a[1])[0];
      const pcfg = s.penalty_config || {};
      return [
        s.supplier_name,
        s.total_receipts,
        s.on_time_pct ?? '',
        s.on_time_count,
        s.late_count,
        s.no_eta_count,
        s.receipts_closed_short,
        s.units_outstanding,
        s.late_credit_due ?? 0,
        pcfg.currency || 'USD',
        pcfg.pct_per_week ?? '',
        pcfg.max_pct ?? '',
        topReason ? `${REASON_LABELS[topReason[0]] || topReason[0]} (${topReason[1]})` : '—',
        s.claims_email_override || '— (default)',
        fmtDate(s.last_incident),
      ];
    }),
  ];
  const csv = rows.map((r) =>
    r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')
  ).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `supplier-shortages-${data.window_days}d-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default function SupplierShortageReport() {
  const [windowDays, setWindowDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSupplier, setExpandedSupplier] = useState(null);

  // Contact-override dialog state
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editPenaltyPct, setEditPenaltyPct] = useState('');
  const [editPenaltyMax, setEditPenaltyMax] = useState('');
  const [editPenaltyCurrency, setEditPenaltyCurrency] = useState('USD');
  const [savingContact, setSavingContact] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/api/reports/supplier-shortages`, {
        params: { window_days: windowDays },
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load supplier shortages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [windowDays]);

  const reasonMix = useMemo(() => {
    if (!data?.totals?.by_reason) return [];
    const total = data.totals.units_outstanding || 1;
    return Object.entries(data.totals.by_reason)
      .map(([k, v]) => ({ key: k, units: v, pct: (v / total) * 100 }))
      .sort((a, b) => b.units - a.units);
  }, [data]);

  const openEdit = (s) => {
    setEditingSupplier(s);
    setEditEmail(s.claims_email_override || '');
    setEditNotes('');
    const pcfg = s.penalty_config || {};
    // Empty strings when default applies — so the user can opt back in
    // by clearing the field.
    setEditPenaltyPct(pcfg.is_default ? '' : String(pcfg.pct_per_week ?? ''));
    setEditPenaltyMax(pcfg.is_default ? '' : String(pcfg.max_pct ?? ''));
    setEditPenaltyCurrency(pcfg.currency || 'USD');
  };

  const saveContact = async () => {
    if (!editingSupplier) return;
    if (editEmail && !/^[^@]+@[^@]+\.[^@]+$/.test(editEmail)) {
      toast.error('Enter a valid email or clear the field to revert to default.');
      return;
    }
    setSavingContact(true);
    try {
      if (editEmail) {
        const pct = editPenaltyPct === '' ? null : parseFloat(editPenaltyPct);
        const maxp = editPenaltyMax === '' ? null : parseFloat(editPenaltyMax);
        if (pct !== null && (Number.isNaN(pct) || pct < 0 || pct > 10)) {
          toast.error('Penalty % per week must be between 0 and 10.');
          setSavingContact(false);
          return;
        }
        if (maxp !== null && (Number.isNaN(maxp) || maxp < 0 || maxp > 50)) {
          toast.error('Penalty cap % must be between 0 and 50.');
          setSavingContact(false);
          return;
        }
        await axios.put(`${API}/api/supplier-contacts`, {
          supplier_name: editingSupplier.supplier_name,
          claims_email: editEmail.trim().toLowerCase(),
          notes: editNotes || null,
          penalty_pct_per_week: pct,
          penalty_max_pct: maxp,
          penalty_currency: (editPenaltyCurrency || 'USD').toUpperCase(),
        }, { withCredentials: true });
        toast.success(`Saved · ${editingSupplier.supplier_name}`);
      } else if (editingSupplier.claims_email_override) {
        await axios.delete(
          `${API}/api/supplier-contacts/${encodeURIComponent(editingSupplier.supplier_key)}`,
          { withCredentials: true },
        );
        toast.success('Override removed — using defaults.');
      }
      setEditingSupplier(null);
      await fetchReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save contact');
    } finally {
      setSavingContact(false);
    }
  };

  const overallOnTime = onTimeChip(data?.totals?.on_time_pct);

  return (
    <Layout>
      <div data-testid="supplier-shortage-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Warning size={28} weight="fill" className="text-amber-600" />
              Supplier scorecard
            </h1>
            <p className="text-muted-foreground mt-1">
              Shortages + on-time delivery per supplier. Use for annual reviews + credit claims.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="supplier-shortage-window-select"
              value={windowDays}
              onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
              className="text-sm border border-border bg-background px-3 py-2 font-mono"
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Button
              variant="outline"
              onClick={fetchReport}
              data-testid="supplier-shortage-refresh"
              className="rounded-none"
              disabled={loading}
            >
              <ArrowClockwise size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => data && downloadCSV(data)}
              disabled={!data?.suppliers?.length}
              data-testid="supplier-shortage-export-csv"
              className="rounded-none"
            >
              <FileArrowDown size={14} className="mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 mb-4 text-sm" data-testid="supplier-shortage-error">
            {error}
          </div>
        )}

        {/* KPI tiles — 5 wide on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
          <div className="bg-card border border-border p-5" data-testid="kpi-suppliers">
            <div className="overline mb-2 flex items-center gap-2"><Buildings size={12} weight="fill" /> Suppliers with issues</div>
            <div className="text-4xl font-bold tabular-nums">{data?.totals?.suppliers_with_shortages ?? '—'}</div>
            <div className="text-xs text-muted-foreground mt-1">in the last {data?.window_days ?? windowDays} days</div>
          </div>
          <div className="bg-card border border-border p-5" data-testid="kpi-receipts">
            <div className="overline mb-2 flex items-center gap-2"><Warning size={12} weight="fill" /> Receipts closed short</div>
            <div className="text-4xl font-bold tabular-nums">{data?.totals?.receipts_closed_short ?? '—'}</div>
            <div className="text-xs text-muted-foreground mt-1">{data?.totals?.units_outstanding ?? 0} units total outstanding</div>
          </div>
          <div className="bg-card border border-border p-5" data-testid="kpi-on-time">
            <div className="overline mb-2 flex items-center gap-2"><Clock size={12} weight="fill" /> On-time delivery</div>
            <div className={`text-4xl font-bold tabular-nums inline-block px-3 py-0 border ${overallOnTime.cls}`}>
              {overallOnTime.label}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {data?.totals?.on_time_count ?? 0} on-time · {data?.totals?.late_count ?? 0} late
              {(data?.totals?.no_eta_count ?? 0) > 0 && (
                <> · <span title="Receipts without an expected_arrival date">{data.totals.no_eta_count} no-ETA</span></>
              )}
            </div>
          </div>
          <div className="bg-card border border-border p-5" data-testid="kpi-late-credit">
            <div className="overline mb-2 flex items-center gap-2"><Money size={12} weight="fill" /> Late credit due</div>
            <div className="text-4xl font-bold tabular-nums text-emerald-700">
              {fmtMoney(data?.totals?.late_credit_due, 'USD')}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Default: {data?.totals?.default_penalty_pct_per_week ?? 0.5}% / week, capped at {data?.totals?.default_penalty_max_pct ?? 5}%
            </div>
          </div>
          <div className="bg-card border border-border p-5" data-testid="kpi-mix">
            <div className="overline mb-2 flex items-center gap-2"><ChartBar size={12} weight="fill" /> Reason mix</div>
            {reasonMix.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">No shortages in window.</div>
            ) : (
              <div className="space-y-1.5 mt-1">
                {reasonMix.map((r) => (
                  <div key={r.key} className="flex items-center gap-2 text-xs" data-testid={`reason-row-${r.key}`}>
                    <div className={`${REASON_COLOURS[r.key] || 'bg-slate-400'} h-2 rounded-sm`} style={{ width: `${Math.max(4, r.pct)}%` }} />
                    <span className="text-muted-foreground whitespace-nowrap font-mono tabular-nums">{r.units}</span>
                    <span className="truncate">{REASON_LABELS[r.key] || r.key}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Supplier table */}
        <div className="bg-card border border-border" data-testid="supplier-shortage-table">
          <div className="overline px-4 py-3 border-b border-border">
            Worst offenders · sorted by units outstanding, tie-break by lowest on-time %
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : !data?.suppliers?.length ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="supplier-shortage-empty">
              No supplier shortages recorded in the last {windowDays} days. Nice run!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Supplier</th>
                    <th className="text-right p-3">Receipts short</th>
                    <th className="text-right p-3">Units outstanding</th>
                    <th className="text-center p-3">On-time %</th>
                    <th className="text-right p-3">Late credit due</th>
                    <th className="text-left p-3">Top reason</th>
                    <th className="text-left p-3">Claims email</th>
                    <th className="text-left p-3">Last incident</th>
                  </tr>
                </thead>
                <tbody>
                  {data.suppliers.map((s) => {
                    const topReason = Object.entries(s.by_reason || {}).sort((a, b) => b[1] - a[1])[0];
                    const isOpen = expandedSupplier === s.supplier_name;
                    const otChip = onTimeChip(s.on_time_pct);
                    return (
                      <React.Fragment key={s.supplier_key || s.supplier_name}>
                        <tr
                          className="border-t border-border cursor-pointer hover:bg-muted"
                          onClick={() => setExpandedSupplier(isOpen ? null : s.supplier_name)}
                          data-testid={`supplier-row-${s.supplier_name}`}
                        >
                          <td className="p-3 font-bold">{s.supplier_name}</td>
                          <td className="p-3 text-right tabular-nums">{s.receipts_closed_short}</td>
                          <td className="p-3 text-right tabular-nums font-bold text-rose-700">{s.units_outstanding}</td>
                          <td className="p-3 text-center">
                            <span
                              className={`inline-block px-2 py-0.5 text-[11px] font-bold border tabular-nums ${otChip.cls}`}
                              title={`${s.on_time_count} on-time / ${s.late_count} late${s.no_eta_count > 0 ? ` · ${s.no_eta_count} no-ETA` : ''}`}
                              data-testid={`supplier-ontime-${s.supplier_name}`}
                            >
                              {otChip.label}
                            </span>
                          </td>
                          <td className="p-3 text-right tabular-nums">
                            {s.late_credit_due > 0 ? (
                              <span
                                className="font-bold text-emerald-700"
                                data-testid={`supplier-credit-${s.supplier_name}`}
                                title={`${s.penalty_config?.pct_per_week ?? 0.5}% / week · cap ${s.penalty_config?.max_pct ?? 5}%${s.penalty_config?.is_default ? ' (default)' : ' (custom)'}`}
                              >
                                {fmtMoney(s.late_credit_due, s.penalty_config?.currency || 'USD')}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-3">
                            {topReason ? (
                              <span className="inline-flex items-center gap-2">
                                <span className={`${REASON_COLOURS[topReason[0]]} h-2 w-2 rounded-full`} />
                                {REASON_LABELS[topReason[0]] || topReason[0]} <span className="text-muted-foreground tabular-nums">({topReason[1]})</span>
                              </span>
                            ) : '—'}
                          </td>
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            {s.claims_email_override ? (
                              <span className="inline-flex items-center gap-1 text-xs">
                                <Envelope size={12} weight="fill" className="text-emerald-700" />
                                <span className="font-mono truncate max-w-[180px]" title={s.claims_email_override}>{s.claims_email_override}</span>
                                <button
                                  className="text-muted-foreground hover:text-foreground p-0.5"
                                  onClick={() => openEdit(s)}
                                  data-testid={`supplier-edit-email-${s.supplier_name}`}
                                  aria-label="Edit claims email"
                                >
                                  <PencilSimple size={12} />
                                </button>
                              </span>
                            ) : (
                              <button
                                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                                onClick={() => openEdit(s)}
                                data-testid={`supplier-set-email-${s.supplier_name}`}
                              >
                                <PencilSimple size={11} />
                                <span className="italic">Use default · set override</span>
                              </button>
                            )}
                          </td>
                          <td className="p-3 text-muted-foreground">{fmtDate(s.last_incident)}</td>
                        </tr>
                        {isOpen && (
                          <tr className="border-t border-border bg-amber-50/30">
                            <td colSpan={8} className="p-4">
                              <div className="overline mb-2">Receipts closed with shortage / late</div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-muted-foreground">
                                    <th className="py-1">Receipt</th>
                                    <th className="py-1">PO</th>
                                    <th className="py-1">Closed</th>
                                    <th className="py-1">ETA</th>
                                    <th className="py-1 text-center">On-time</th>
                                    <th className="py-1 text-right">Outstanding</th>
                                    <th className="py-1 text-right">Late credit</th>
                                    <th className="py-1">Reason</th>
                                    <th className="py-1">Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(data.receipts || [])
                                    .filter((r) => r.supplier_name === s.supplier_name)
                                    .map((r) => (
                                      <tr key={r.id} className="border-t border-border/60" data-testid={`shortage-receipt-${r.receipt_number}`}>
                                        <td className="py-1 font-mono">{r.receipt_number}</td>
                                        <td className="py-1 font-mono text-muted-foreground">{r.po_number || '—'}</td>
                                        <td className="py-1 text-muted-foreground">{fmtDate(r.completed_at)}</td>
                                        <td className="py-1 text-muted-foreground">{fmtDate(r.expected_arrival)}</td>
                                        <td className="py-1 text-center">
                                          {r.on_time === 'on_time' && <CheckCircle size={14} weight="fill" className="inline text-emerald-600" />}
                                          {r.on_time === 'late' && <XCircle size={14} weight="fill" className="inline text-rose-600" />}
                                          {r.on_time === 'no_eta' && <span className="text-[10px] text-muted-foreground">—</span>}
                                        </td>
                                        <td className="py-1 text-right font-mono font-bold text-rose-700 tabular-nums">{r.outstanding_qty}</td>
                                        <td className="py-1 text-right font-mono tabular-nums">
                                          {r.late_credit_due > 0 ? (
                                            <span className="text-emerald-700 font-bold" title={`${r.weeks_late} weeks late · PO value ${fmtMoney(r.receipt_value, r.penalty_currency)}`}>
                                              {fmtMoney(r.late_credit_due, r.penalty_currency)}
                                            </span>
                                          ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="py-1">{REASON_LABELS[r.shortage_reason] || r.shortage_reason}</td>
                                        <td className="py-1 text-muted-foreground truncate max-w-[260px]" title={r.shortage_notes || ''}>{r.shortage_notes || '—'}</td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground mt-3 font-mono">
          Window: last {data?.window_days ?? windowDays} days · As of {data?.as_of ? fmtDate(data.as_of) : '—'} · {data?.totals?.total_receipts ?? 0} total receipts in window · Click a supplier row to expand.
        </p>
      </div>

      {/* Contact override dialog */}
      <Dialog
        open={!!editingSupplier}
        onOpenChange={(v) => { if (!v) setEditingSupplier(null); }}
      >
        <DialogContent className="border border-border rounded-none" data-testid="supplier-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">
              Claims email · {editingSupplier?.supplier_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
              <p className="font-bold uppercase tracking-widest text-[10px] mb-1">How this works</p>
              <p>
                Auto-claim emails for this supplier will route to the address below
                instead of the default <span className="font-mono">SUPPLIER_CLAIMS_INBOX</span>.
                Leave the field empty and save to remove the override.
              </p>
            </div>
            <div>
              <Label className="overline">Claims email address</Label>
              <Input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="claims@example.com — or leave blank to use default"
                className="mt-1 font-mono"
                data-testid="supplier-contact-email-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="overline">Notes <span className="font-normal lowercase tracking-normal text-muted-foreground">(optional, internal)</span></Label>
              <Input
                type="text"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="e.g. Use AP contact, NOT sales rep"
                className="mt-1"
                data-testid="supplier-contact-notes-input"
              />
            </div>

            {/* Iter139 — late-delivery penalty config */}
            <div className="border-t border-border pt-4">
              <div className="overline mb-2 flex items-center gap-1.5">
                <Money size={11} weight="fill" /> Late-delivery penalty
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">
                Defaults to <strong>0.5% per week late, capped at 5%</strong> of PO value. Override to match your contract.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="overline" htmlFor="penalty-pct">% / week</Label>
                  <Input
                    id="penalty-pct"
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={editPenaltyPct}
                    onChange={(e) => setEditPenaltyPct(e.target.value)}
                    placeholder="0.5"
                    className="mt-1 tabular-nums"
                    data-testid="penalty-pct-input"
                  />
                </div>
                <div>
                  <Label className="overline" htmlFor="penalty-max">Cap %</Label>
                  <Input
                    id="penalty-max"
                    type="number"
                    step="0.5"
                    min="0"
                    max="50"
                    value={editPenaltyMax}
                    onChange={(e) => setEditPenaltyMax(e.target.value)}
                    placeholder="5"
                    className="mt-1 tabular-nums"
                    data-testid="penalty-cap-input"
                  />
                </div>
                <div>
                  <Label className="overline" htmlFor="penalty-ccy">Currency</Label>
                  <Input
                    id="penalty-ccy"
                    type="text"
                    maxLength={3}
                    value={editPenaltyCurrency}
                    onChange={(e) => setEditPenaltyCurrency(e.target.value.toUpperCase())}
                    placeholder="USD"
                    className="mt-1 uppercase font-mono"
                    data-testid="penalty-currency-input"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {editingSupplier?.claims_email_override && (
                <Button
                  variant="outline"
                  onClick={() => { setEditEmail(''); }}
                  className="rounded-none text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid="supplier-contact-clear-btn"
                >
                  <Trash size={14} className="mr-1" /> Clear
                </Button>
              )}
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={() => setEditingSupplier(null)}
                disabled={savingContact}
                className="rounded-none"
                data-testid="supplier-contact-cancel-btn"
              >Cancel</Button>
              <Button
                onClick={saveContact}
                disabled={savingContact}
                className="bg-brand hover:bg-brand/90 text-white rounded-none"
                data-testid="supplier-contact-save-btn"
              >{savingContact ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
